import { LoadingOutlined } from "@ant-design/icons";
import { Spin, type SpinProps } from "antd";
import type React from "react";
import styled, { keyframes } from "styled-components";

import { colors } from "styles/colors";
import { styleAsClass } from "styles/styleAsClass";

const LoadingIcon = styled(LoadingOutlined)<{ color?: string }>`
  color: ${(props) => props.color ?? props.theme.colors.ACCENT_BLUE_500};
`;

/**
 * Tracked spinner footprint — matches 16×16 button icons (e.g. Clark publish DeployIcon),
 * not antd's 14px `small` dot.
 */
const TRACKED_SPINNER_DIAMETER_PX: Record<
  NonNullable<SpinProps["size"]>,
  number
> = {
  small: 16,
  default: 20,
  large: 32,
};

/** 1px stroke for tracked ring + arc. */
const TRACKED_SPINNER_STROKE_PX = 1;
/** Inset per side so the ring sits inside the icon footprint (16px box → 13px ring). */
const TRACKED_SPINNER_RING_INSET_PX = 1.5;
/** Arc length as a fraction of circumference — similar visual weight to LoadingOutlined. */
const TRACKED_SPINNER_ARC_RATIO = 0.72;

const trackedSpinnerRotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

/** Ant Design centers `.ant-spin-dot` for the default indicator; flex-center custom SVG rings. */
const TrackedSpinOverlayClass = styleAsClass`
  &.ant-spin-nested-loading > div > .ant-spin {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  &.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot {
    height: auto;
    left: auto;
    margin: 0;
    position: static;
    top: auto;
    width: auto;
  }
`;

const TrackedSpinnerRoot = styled.svg<{ $spinning: boolean }>`
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;

  .tracked-spinner-arc {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${trackedSpinnerRotate} 1s linear infinite;
    animation-play-state: ${({ $spinning }) =>
      $spinning ? "running" : "paused"};
  }
`;

function TrackedSpinnerIcon({
  diameter,
  color,
  trackColor,
  spinning = true,
}: {
  diameter: number;
  color: string;
  trackColor: string;
  spinning?: boolean;
}) {
  const strokeWidth = TRACKED_SPINNER_STROKE_PX;
  const center = diameter / 2;
  const ringDiameter = diameter - TRACKED_SPINNER_RING_INSET_PX * 2;
  const radius = (ringDiameter - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * TRACKED_SPINNER_ARC_RATIO;

  return (
    <TrackedSpinnerRoot
      $spinning={spinning}
      width={diameter}
      height={diameter}
      viewBox={`0 0 ${diameter} ${diameter}`}
      role="status"
      aria-label="Loading"
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={strokeWidth}
      />
      <g
        className="tracked-spinner-arc"
        transform={`rotate(-90 ${center} ${center})`}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${arcLength} ${circumference}`}
        />
      </g>
    </TrackedSpinnerRoot>
  );
}

export const Spinner = (
  props: SpinProps & {
    color?: string;
    /** Ring behind the indicator; stays fixed while the icon spins. */
    trackColor?: string;
    children?: React.ReactNode;
  },
) => {
  const {
    color,
    trackColor,
    children,
    size = "default",
    spinning = true,
    wrapperClassName,
    ...spinProps
  } = props;
  const indicatorColor = color ?? colors.ACCENT_BLUE_500;

  if (trackColor) {
    const diameter = TRACKED_SPINNER_DIAMETER_PX[size];
    const indicator = (
      <TrackedSpinnerIcon
        diameter={diameter}
        color={indicatorColor}
        trackColor={trackColor}
        spinning={spinning}
      />
    );

    if (!children) {
      return indicator;
    }

    return (
      <Spin
        indicator={indicator}
        size={size}
        spinning={spinning}
        wrapperClassName={
          [TrackedSpinOverlayClass, wrapperClassName]
            .filter(Boolean)
            .join(" ") || undefined
        }
        {...spinProps}
      >
        {children}
      </Spin>
    );
  }

  return (
    <Spin
      indicator={<LoadingIcon color={color} />}
      size={size}
      spinning={spinning}
      {...spinProps}
    >
      {children}
    </Spin>
  );
};
